/**
 * Speaker Profile Manager
 *
 * Unified component for managing conversation partners (people the user has
 * conversations with). Displays a list of partners with edit/delete capabilities,
 * voice profile status indicators, and the ability to add new partners.
 *
 * Can be used in the Account page or as a modal.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import {
  useConversationPartners,
  useCreateConversationPartner,
  useUpdateConversationPartner,
  useDeleteConversationPartner,
  useSetPrimaryPartner,
} from "../../hooks/useConversationPartners";
import { useCurrentUser, useProfileOptions } from "../../hooks/useCurrentUser";
import {
  Loader2,
  Plus,
  Pencil,
  Trash2,
  Star,
  Mic,
  MicOff,
  Brain,
  User,
  Users,
} from "lucide-react";
import type {
  ConversationPartner,
  ConversationPartnerType,
} from "../../types/conversationPartner";
import { PARTNER_TYPE_LABELS } from "../../types/conversationPartner";

interface SpeakerProfileManagerProps {
  /** If true, displays in a more compact format */
  compact?: boolean;
  /** Callback when a partner is selected (for modal use) */
  onSelect?: (partner: ConversationPartner) => void;
}

export function SpeakerProfileManager({
  compact = false,
  onSelect,
}: SpeakerProfileManagerProps) {
  const { data: user } = useCurrentUser();
  const { data: partnersData, isLoading, error } = useConversationPartners();
  const { data: profileOptions } = useProfileOptions();
  const createPartner = useCreateConversationPartner();
  const updatePartner = useUpdateConversationPartner();
  const deletePartner = useDeleteConversationPartner();
  const setPrimary = useSetPrimaryPartner();

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingPartner, setEditingPartner] =
    useState<ConversationPartner | null>(null);
  const [deletingPartner, setDeletingPartner] =
    useState<ConversationPartner | null>(null);

  // Form state
  const [formName, setFormName] = useState("");
  const [formPronouns, setFormPronouns] = useState("");
  const [formType, setFormType] =
    useState<ConversationPartnerType>("primary_partner");
  const [formIsPrimary, setFormIsPrimary] = useState(false);

  const resetForm = () => {
    setFormName("");
    setFormPronouns("");
    setFormType("primary_partner");
    setFormIsPrimary(false);
  };

  const openAddDialog = () => {
    resetForm();
    // Default to primary if no partners exist
    if (!partnersData?.partners.length) {
      setFormIsPrimary(true);
    }
    setIsAddDialogOpen(true);
  };

  const openEditDialog = (partner: ConversationPartner) => {
    setFormName(partner.name);
    setFormPronouns(partner.pronouns || "");
    setFormType(partner.partner_type);
    setEditingPartner(partner);
  };

  const handleCreate = async () => {
    try {
      await createPartner.mutateAsync({
        name: formName,
        pronouns: formPronouns || undefined,
        partner_type: formType,
        is_primary: formIsPrimary,
      });
      setIsAddDialogOpen(false);
      resetForm();
    } catch {
      // Error handled by mutation state
    }
  };

  const handleUpdate = async () => {
    if (!editingPartner) return;
    try {
      await updatePartner.mutateAsync({
        partnerId: editingPartner.id,
        data: {
          name: formName,
          pronouns: formPronouns || undefined,
          partner_type: formType,
        },
      });
      setEditingPartner(null);
      resetForm();
    } catch {
      // Error handled by mutation state
    }
  };

  const handleDelete = async () => {
    if (!deletingPartner) return;
    try {
      await deletePartner.mutateAsync(deletingPartner.id);
      setDeletingPartner(null);
    } catch {
      // Error handled by mutation state
    }
  };

  const handleSetPrimary = async (partner: ConversationPartner) => {
    try {
      await setPrimary.mutateAsync(partner.id);
    } catch {
      // Error handled by mutation state
    }
  };

  if (isLoading) {
    return (
      <Card className={compact ? "p-4" : "p-6"}>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={compact ? "p-4" : "p-6"}>
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
          <p className="text-sm text-destructive">
            Failed to load conversation partners
          </p>
        </div>
      </Card>
    );
  }

  const partners = partnersData?.partners || [];

  return (
    <>
      <Card className={compact ? "p-4" : "p-6"}>
        {/* Header */}
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2
              className={`font-semibold text-foreground ${compact ? "text-base" : "text-lg"}`}
            >
              <Users className="mr-2 inline h-5 w-5" />
              Conversation Partners
            </h2>
            {!compact && (
              <p className="mt-1 text-sm text-muted-foreground">
                People you have conversations with. Your primary partner is used
                by default for new reflections.
              </p>
            )}
          </div>
          <Button
            onClick={openAddDialog}
            size={compact ? "sm" : "default"}
            disabled={partners.length >= 10}
          >
            <Plus className="mr-1 h-4 w-4" />
            Add
          </Button>
        </div>

        {/* Your profile summary */}
        {!compact && user && (
          <div className="mb-4 rounded-lg border bg-muted/30 p-3">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                <User className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-medium text-foreground">
                  {user.first_name || "You"}{" "}
                  <span className="text-xs text-muted-foreground">(You)</span>
                </p>
                {user.pronouns && (
                  <p className="text-sm text-muted-foreground">
                    {user.pronouns}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Partners list */}
        {partners.length === 0 ? (
          <div className="py-8 text-center">
            <Users className="mx-auto mb-3 h-12 w-12 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">
              No conversation partners yet.
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              Add your partner to get started with personalized speaker
              identification.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {partners.map((partner) => (
              <PartnerCard
                key={partner.id}
                partner={partner}
                compact={compact}
                onEdit={() => openEditDialog(partner)}
                onDelete={() => setDeletingPartner(partner)}
                onSetPrimary={() => handleSetPrimary(partner)}
                onSelect={onSelect ? () => onSelect(partner) : undefined}
                isSettingPrimary={
                  setPrimary.isPending && setPrimary.variables === partner.id
                }
              />
            ))}
          </div>
        )}

        {partners.length >= 10 && (
          <p className="mt-4 text-xs text-muted-foreground">
            Maximum of 10 conversation partners allowed.
          </p>
        )}
      </Card>

      {/* Add Partner Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Conversation Partner</DialogTitle>
            <DialogDescription>
              Add someone you have conversations with. This helps identify
              speakers in your reflections.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium">Name</label>
              <Input
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="Enter their name"
                className="mt-1"
              />
            </div>
            <div>
              <label className="block text-sm font-medium">Pronouns</label>
              <Select value={formPronouns} onValueChange={setFormPronouns}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select pronouns" />
                </SelectTrigger>
                <SelectContent>
                  {profileOptions?.pronouns.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium">Relationship</label>
              <Select
                value={formType}
                onValueChange={(v) => setFormType(v as ConversationPartnerType)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(PARTNER_TYPE_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {partners.length === 0 && (
              <p className="text-sm text-muted-foreground">
                This will be set as your primary partner.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={!formName.trim() || createPartner.isPending}
            >
              {createPartner.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Add Partner
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Partner Dialog */}
      <Dialog
        open={!!editingPartner}
        onOpenChange={(open) => !open && setEditingPartner(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Partner</DialogTitle>
            <DialogDescription>
              Update the information for this conversation partner.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium">Name</label>
              <Input
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="Enter their name"
                className="mt-1"
              />
            </div>
            <div>
              <label className="block text-sm font-medium">Pronouns</label>
              <Select value={formPronouns} onValueChange={setFormPronouns}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select pronouns" />
                </SelectTrigger>
                <SelectContent>
                  {profileOptions?.pronouns.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium">Relationship</label>
              <Select
                value={formType}
                onValueChange={(v) => setFormType(v as ConversationPartnerType)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(PARTNER_TYPE_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingPartner(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdate}
              disabled={!formName.trim() || updatePartner.isPending}
            >
              {updatePartner.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={!!deletingPartner}
        onOpenChange={(open) => !open && setDeletingPartner(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deletingPartner?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Reflections involving {deletingPartner?.name} will be kept but
              unlinked from any partner. Their memory will be deleted. This
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deletePartner.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

interface PartnerCardProps {
  partner: ConversationPartner;
  compact?: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onSetPrimary: () => void;
  onSelect?: () => void;
  isSettingPrimary: boolean;
}

function PartnerCard({
  partner,
  compact,
  onEdit,
  onDelete,
  onSetPrimary,
  onSelect,
  isSettingPrimary,
}: PartnerCardProps) {
  const hasVoiceProfile = !!partner.voice_profile;

  return (
    <div
      className={`flex items-center justify-between rounded-lg border p-3 ${
        onSelect ? "cursor-pointer hover:bg-muted/50" : ""
      }`}
      onClick={onSelect}
    >
      <div className="flex items-center gap-3">
        <div
          className={`flex h-10 w-10 items-center justify-center rounded-full ${
            partner.is_primary ? "bg-yellow-100 dark:bg-yellow-900" : "bg-muted"
          }`}
        >
          {partner.is_primary ? (
            <Star className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
          ) : (
            <User className="h-5 w-5 text-muted-foreground" />
          )}
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-foreground">{partner.name}</span>
            {partner.is_primary && (
              <span className="rounded bg-yellow-100 dark:bg-yellow-900 px-1.5 py-0.5 text-xs font-medium text-yellow-800 dark:text-yellow-200">
                Primary
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            {partner.pronouns && <span>{partner.pronouns}</span>}
            {!compact && (
              <>
                <span>-</span>
                <span>{PARTNER_TYPE_LABELS[partner.partner_type]}</span>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1">
        {/* Voice profile indicator */}
        <div
          className="mr-2 flex items-center gap-1"
          title={
            hasVoiceProfile
              ? `Voice trained (${partner.voice_profile?.sample_count} samples)`
              : "No voice profile"
          }
        >
          {hasVoiceProfile ? (
            <Mic className="h-4 w-4 text-green-600 dark:text-green-400" />
          ) : (
            <MicOff className="h-4 w-4 text-muted-foreground" />
          )}
        </div>

        <Link
          to={`/partners/${partner.id}`}
          onClick={(e) => e.stopPropagation()}
          title="View memory"
        >
          <Button variant="ghost" size="icon">
            <Brain className="h-4 w-4" />
          </Button>
        </Link>
        {!partner.is_primary && (
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              onSetPrimary();
            }}
            disabled={isSettingPrimary}
            title="Set as primary"
          >
            {isSettingPrimary ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Star className="h-4 w-4" />
            )}
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
          title="Edit"
        >
          <Pencil className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Delete partner"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          title="Delete"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
